import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { PoweredBy } from 'components/ui';
import dayjs, { Dayjs } from 'dayjs';
import { EcommerceEventsHelper } from 'shared/helpers/ecommerce';
import datetime from 'utils/datetime';

import { getBookingBlockTimes, makeBooking } from '../../../../api/booking';
import useBookingRootLink from '../../../../hooks/useBookingRootLink';
import useUTM from '../../../../hooks/useUTM';
import { useCachedCustomerInfo } from '../../../../providers/CustomerInfo/hooks';
import i18n from '../../../../providers/Locale';
import useLanguage from '../../../../providers/Locale/hooks';
import usePlace from '../../../../providers/Place/hooks';
import { DEFAULT_INTERVAL, MINUTES_IN_HOUR } from '../../../../shared/time';
import ComponentType from '../../../../types/Component';
import LocalStorageData from '../../../../types/LocalStorageData';
import Translation from '../../../../types/Translation';
import {
	BlockedTimeResponse,
	BookingPreferredArea,
	BookingSettings,
	MakeBookingType,
} from '../../../../types/booking';
import storageValue from '../../../../utils/storageValue';
import {
	validateEmail,
	validatePhoneNumber,
	validateRequiredField,
} from '../../../../utils/validation';
import PostMessageListener, {
	PostMessageDataOutgoingType,
	PostMessageInstance,
	PostMessageOutgoingDataAction,
} from '../../../PostMessageListener';
import WarningBlock from '../../../WarningBlock';
import Button from '../../../ui/Button';
import Dialog from '../../../ui/Dialog';
import { CustomSelectOption } from '../../../ui/Select';
import { BlockTimeDurationModal } from '../BlockTimeDurationModal/BlockTimeDurationModal';
import PaymentError from '../PaymentError';
import Steps from '../Steps';

import StepFirst from './StepFirst';
import StepSecond from './StepSecond';
import { WideSelectOption } from './WideSelect';
import style from './style.module.scss';

type FormProps = {
	bookingSettings: BookingSettings;
};

type FormDataType = {
	date: string;
	personCount: string;
	time: string;
	name: string;
	email: string;
	phone: string;
	comment: string;
	visitDuration: string;
	allowMarketing: boolean;
	preferArea: string;
};

type StateRefType = {
	isActiveDayInited: boolean;
	isInited: boolean;
	isLoadingBookingBlockDates: boolean;
	bookCheckDayId: number;
};

export type FormInputsValue = Partial<FormDataType>;

export type FormInputsError = Partial<
	Record<
		| 'date'
		| 'personCount'
		| 'time'
		| 'name'
		| 'email'
		| 'phone'
		| 'comment'
		| 'visitDuration'
		| 'preferArea',
		string
	>
>;

const _getHoursIntervalOptions = (
	maxHours: number,
	minHours: number,
	interval = DEFAULT_INTERVAL
): CustomSelectOption[] => {
	const _maxHours = maxHours / MINUTES_IN_HOUR;
	const timeOptions = [...new Array((MINUTES_IN_HOUR / interval) * _maxHours)]
		.map((el, index) => {
			const minutesValue = (index + 1) * interval;
			const hoursValue = +String(Math.floor(minutesValue / MINUTES_IN_HOUR));
			const minutesString = String(minutesValue % MINUTES_IN_HOUR);
			const hours = hoursValue ? `~ ${hoursValue} ${i18n('hour')}` : '';
			const minutes = +minutesString ? `${minutesString} ${i18n('minute')}` : '';

			if (minutesValue < minHours) {
				return undefined;
			}
			return { value: String(minutesValue), label: `${hours} ${minutes}` };
		})
		.filter((item) => item);
	return timeOptions as CustomSelectOption[];
};

const _getPersonCountOptions = (count: number): CustomSelectOption[] => {
	return [...new Array(count)].map((el, index) => ({
		value: String(index + 1),
		label: String(index + 1),
	}));
};

enum FormStep {
	FIRST,
	SECOND,
	PAY_WARNING,
	ERROR,
}

const Form: ComponentType<FormProps> = ({ bookingSettings }) => {
	const { maxVisitDuration, minVisitDuration } = bookingSettings;
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const bookingRootLink = useBookingRootLink();
	const paymentError = searchParams.get('error');
	const { currentLanguage } = useLanguage();
	const { state: utmState } = useUTM();

	const [stepNumber, setStepNumber] = useState<FormStep>(
		paymentError ? FormStep.ERROR : FormStep.FIRST
	);
	const [redirectUrl, setRedirectUrl] = useState<string>('');
	const { place } = usePlace();
	const [error, setError] = useState<string>('');
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [selectedDay, setSelectedDay] = useState<string>();
	const [formState, setFormState] = useState<FormInputsValue>();

	const stateRef = useRef<StateRefType>({
		isInited: false,
		isLoadingBookingBlockDates: false,
		bookCheckDayId: 0,
		isActiveDayInited: false,
	});

	const nearestWorkingDay = useMemo(
		() =>
			datetime.schedule.getNearestBusinessDay({
				workTimeAll: place?.workTime,
				exceptions: bookingSettings.workingHours?.exceptions,
			}),
		[]
	);
	const visitDurationOptions: CustomSelectOption[] = useMemo(
		() => _getHoursIntervalOptions(maxVisitDuration, minVisitDuration),
		[maxVisitDuration, minVisitDuration]
	);
	const personCountOptions: CustomSelectOption[] = useMemo(
		() => _getPersonCountOptions(bookingSettings.maxPeopleCount ?? 10),
		[bookingSettings.maxPeopleCount]
	);
	const [locationSelector, setLocationSelector] = useState<boolean>(false);
	const [preferredArea, setPreferredArea] = useState<BookingPreferredArea[]>([]);
	const [blockedTime, setBlockedTime] = useState<BlockedTimeResponse>();
	const [oneDayBlockedTime, setOneDayBlockedTime] = useState<BlockedTimeResponse | undefined>();
	const [timeDurationConflict, setTimeDurationConflict] = useState(false);

	const [postMessageInstance, setPostMessageInstance] = useState<PostMessageInstance | null>(null);

	const dayOptions: WideSelectOption[] = useMemo(() => {
		if (!place) return [];
		return datetime.options.booking.getDayOptions({
			workTimeAll: place?.workTime,
			bookingSettings,
			blockedTimes: blockedTime?.blocks,
		});
	}, [bookingSettings.maxDaysBefore, place?.workTime, blockedTime, currentLanguage]);

	const { cachedCustomerInfo, updateCachedCustomerInfo } = useCachedCustomerInfo();
	const [formError, setFormError] = useState<FormInputsError>({});

	const getTimeOptions = (date: string) => {
		if (!place) return [];

		return datetime.options.booking.getTimeOptions({
			bookingSettings,
			date,
			blockedTimes: (oneDayBlockedTime || blockedTime)?.blocks,
			workTimeAll: place?.workTime,
			visitDuration: formState?.visitDuration ? parseInt(formState?.visitDuration, 10) : undefined,
		});
	};

	const timeOptions = useMemo(() => {
		if (!bookingSettings) return null;

		let date: string =
			(selectedDay || formState?.date) ??
			dayjs(nearestWorkingDay ?? dayOptions?.find((_option) => !_option.disabled)?.value).format(
				'YYYY-MM-DD'
			);

		let result = getTimeOptions(date);

		if (!stateRef.current.isActiveDayInited) {
			while ((result && result.length <= 0) || !result?.find((i) => !i.disabled)) {
				const currentDay = dayOptions?.find(({ value }) => value === date);
				if (!currentDay || !dayOptions) break;
				const index = dayOptions.indexOf(currentDay);
				const nextDay = dayOptions.find((item, i) => i > index && !item.disabled);

				if (!nextDay) break;
				date = nextDay.value;
				result = getTimeOptions(nextDay.value);
				setFormState((data) => ({ ...data, ...{ date: nextDay.value } }));
			}
		}

		return result;
	}, [
		bookingSettings,
		place,
		selectedDay,
		oneDayBlockedTime,
		dayOptions,
		formState?.date,
		formState?.visitDuration,
		stateRef.current.isActiveDayInited,
	]);

	const clearErrorInput = (name: string) => {
		setFormError({ ...formError, [name]: undefined });
	};

	const onInputChange = useCallback(
		(value: string, name: string) => {
			setFormState((state) => ({ ...state, [name]: value }));
			clearErrorInput(name);

			if (name === 'time' || name === 'visitDuration') {
				handleCheckTimeDurationConflict({ [name]: value });
			}

			if (name === 'date') {
				setSelectedDay(value);
			}

			if (['name', 'email', 'phone'].includes(name)) {
				updateCachedCustomerInfo({ [name]: value });
			}
		},
		[formState, formError]
	);

	const onInputFieldChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const target = event.currentTarget;
			onInputChange(target.value, target.name!);
		},
		[formState, formError, onInputChange]
	);

	const onContinueBtnClick = useCallback(() => {
		const requiredMessage = i18n('required_field');
		const errors: FormInputsError = {};

		if (!formState?.date) errors.date = requiredMessage;
		if (!formState?.personCount) errors.personCount = requiredMessage;
		if (!formState?.time) errors.time = requiredMessage;
		if (!formState?.visitDuration) errors.visitDuration = requiredMessage;
		if (locationSelector && !formState?.preferArea) errors.preferArea = requiredMessage;

		setFormError((currentErrors) => ({ ...currentErrors, ...errors }));

		if (Object.keys(errors).length) return;

		const isThereTimeDurationConflicts = handleCheckTimeDurationConflict();

		if (isThereTimeDurationConflicts) return;

		setStepNumber(FormStep.SECOND);
	}, [formState]);

	const sendFormData = async () => {
		if (!formState) return;

		const {
			name,
			phone,
			email,
			time,
			personCount,
			comment,
			visitDuration,
			preferArea,
			allowMarketing,
		} = formState;

		const payload: MakeBookingType = {
			personCount: Number(personCount),
			comment,
			name: name!,
			phone: phone!,
			email: email?.trim() || null,
			dateTime: time ?? dayjs().format('YYYY-MM-DD HH:mm'),
			langCode: currentLanguage,
			visitDuration: Number(visitDuration),
			preferArea,
			allowMarketing,
			marketing: {
				utm: {
					source: 'Widget',
				},
			},
		};

		if (utmState) {
			if (!payload.marketing) payload.marketing = {};
			payload.marketing.utm = utmState;
		}

		const { result, error } = await makeBooking(payload);

		if (result) {
			postMessageInstance?.postMessage({
				action: PostMessageOutgoingDataAction.bookingCreated,
				data: {
					booking: payload,
				},
			} as PostMessageDataOutgoingType);
			const currentStorageValue = JSON.parse(storageValue(LocalStorageData.booking));
			const newStorageValue = JSON.stringify(
				currentStorageValue ? [...currentStorageValue, result] : [result]
			);

			storageValue(LocalStorageData.booking, newStorageValue);

			if (result.redirectUrl) {
				window.open(result.redirectUrl);
				setStepNumber(FormStep.PAY_WARNING);
				setRedirectUrl(result.redirectUrl);
				return;
			}

			EcommerceEventsHelper.addBookingRequest({ booking: result });
			navigate(`${bookingRootLink}/${result.guid}/`);
		} else if (error) {
			setError(i18n(error.message as keyof Translation) || i18n('unexpected_error'));
		}

		setIsLoading(false);
	};

	const onChangeMarketing = (state: boolean) => {
		setFormState((data) => ({ ...data, allowMarketing: state }));
	};

	const onReserveBtnClick = useCallback(async () => {
		const errors: FormInputsError = {};
		const nameValidation = validateRequiredField(formState?.name);
		const phoneValidation = validatePhoneNumber(formState?.phone);
		const emailValidation = validateEmail(formState?.email, bookingSettings.requiredEmailBooking);

		if (!nameValidation.isValid) errors.name = nameValidation.message;
		if (!phoneValidation.isValid) errors.phone = phoneValidation.message;
		if (!emailValidation.isValid) errors.email = emailValidation.message;

		setFormError((currentErrors) => ({ ...currentErrors, ...errors }));

		if (Object.keys(errors).length) return;

		setIsLoading(true);
		await sendFormData();
	}, [formState]);

	const fetchBlockedTimeForMonth = useCallback(async () => {
		try {
			const { result } = await getBookingBlockTimes();

			if (result) {
				setBlockedTime(result);
			}
		} catch (e: any) {
			console.warn(e);
		}
	}, []);

	const fetchBlockedTimeForDay = useCallback(
		async (date?: string) => {
			if (!date) return;
			if (!blockedTime) return;

			const requestedDate = dayjs(date);
			const lastBlockedDate = dayjs(blockedTime.till);

			if (requestedDate > lastBlockedDate) {
				try {
					const { result } = await getBookingBlockTimes(date);

					if (result) {
						setOneDayBlockedTime(result);
					}
				} catch (e: any) {
					console.warn(e);
				}
			} else {
				setOneDayBlockedTime(undefined);
			}
		},
		[blockedTime]
	);

	const updateStateRef = (data: Partial<StateRefType>) => {
		stateRef.current = { ...stateRef.current, ...data };
	};

	const getExceptionDate = (_date: Dayjs) => {
		const dayException = bookingSettings?.workingHours?.exceptions?.find((exception) =>
			datetime.helpers.getIsDateBetweenWorkTime({ date: _date, workTime: exception })
		);

		return dayException
			? datetime.helpers.getDateFromWorkTime({
					workTime: { from: dayException.from, till: dayException.till },
					date: new Date(dayException.date),
			  })
			: undefined;
	};

	const handleCheckTimeDurationConflict = (props?: { time?: string; visitDuration?: string }) => {
		const time = props?.time ?? formState?.time;
		const duration = props?.visitDuration ?? formState?.visitDuration;
		const blockedOptions = (timeOptions as WideSelectOption[])
			.filter((option) => option.disabled)
			.map((option) => option.value);

		const _workTime = place?.workTime[dayjs(time).day()];

		if (!time || !duration || !_workTime) return false;
		let isBlocked = false;

		const timeWithDuration = dayjs(time).add(parseInt(duration), 'minutes');
		const businessPeriod = datetime.helpers.getDateFromWorkTime({
			date: new Date(time),
			workTime: _workTime,
		});
		const exceptionDate = getExceptionDate(timeWithDuration);

		const maxAvailableTime = dayjs(exceptionDate?.till ?? businessPeriod.till);

		if (timeWithDuration.isAfter(maxAvailableTime)) {
			isBlocked = true;
			setTimeDurationConflict(true);
			return isBlocked;
		}

		const timeDateWithDuration = dayjs(time).add(parseInt(duration), 'minutes');
		const timeDate = dayjs(time);

		blockedOptions.forEach((option) => {
			const optionDate = dayjs(option);

			if (timeDate.isBefore(optionDate) && optionDate.isBefore(timeDateWithDuration)) {
				isBlocked = true;
			}
		});

		setTimeDurationConflict(isBlocked);

		return isBlocked;
	};

	const handleCloseTimeDurationConflictModal = () => {
		setFormError((currentErrors) => ({ ...currentErrors, time: ' ' }));
		setFormState((prevState) => ({ ...prevState, time: undefined }));
		setTimeDurationConflict(false);
	};

	useEffect(() => {
		if (!formState) return;
		blockedTime ? fetchBlockedTimeForDay(formState.date) : fetchBlockedTimeForMonth();
	}, [formState?.date]);

	useEffect(() => {
		if (timeOptions?.length) {
			setFormState((state) => {
				return { ...state, time: timeOptions.find((option) => !option.disabled)?.value };
			});
		}
	}, [timeOptions]);

	useEffect(() => {
		if (
			bookingSettings &&
			(nearestWorkingDay !== null || dayOptions !== null) &&
			visitDurationOptions !== null &&
			personCountOptions !== null &&
			timeOptions !== null &&
			!stateRef.current.isInited
		) {
			updateStateRef({ isInited: true });

			setFormState({
				personCount: personCountOptions[1]?.value,
				visitDuration: String(bookingSettings.visitDuration || visitDurationOptions[0]?.value),
				date: dayOptions?.find((_option) => !_option.disabled)?.value,
				time: timeOptions?.[0]?.value ?? '',
				name: cachedCustomerInfo.name ?? '',
				email: cachedCustomerInfo.email ?? '',
				phone: cachedCustomerInfo.phone ?? '',
				allowMarketing: true,
			});
		}
	}, [
		bookingSettings,
		nearestWorkingDay,
		visitDurationOptions,
		personCountOptions,
		dayOptions,
		timeOptions,
	]);

	useEffect(() => {
		if (timeOptions?.length) {
			setFormState((state) => {
				const _selected = timeOptions.find((option) => option.value === formState?.time);

				return {
					...state,
					time: _selected?.value ?? timeOptions.find((option) => !option.disabled)?.value,
				};
			});
		}
	}, [timeOptions]);

	useEffect(() => {
		setStepNumber(searchParams.get('error') ? FormStep.ERROR : FormStep.FIRST);
	}, [searchParams]);

	if (!formState) return null;

	return (
		<div className={style.Form}>
			<PostMessageListener
				postMessageInstance={postMessageInstance}
				setPostMessageInstance={setPostMessageInstance}
				formState={formState}
				onFormChange={onInputChange}
				dayOptions={dayOptions}
				timeOptions={timeOptions}
			/>
			<Steps
				isFirstStep={stepNumber === FormStep.FIRST}
				onFirstStepClick={() => setStepNumber(FormStep.FIRST)}
			/>
			<div className={style.form}>
				{stepNumber === FormStep.FIRST && (
					<StepFirst
						bookingSettings={bookingSettings}
						formState={formState}
						formError={formError}
						onInputChange={onInputChange}
						dayOptions={dayOptions}
						timeOptions={timeOptions}
						visitDurationOptions={visitDurationOptions}
						personCountOptions={personCountOptions}
						onContinueBtnClick={onContinueBtnClick}
						locationSelector={locationSelector}
						setLocationSelector={setLocationSelector}
						preferredArea={preferredArea}
						setPreferredArea={setPreferredArea}
					/>
				)}
				{stepNumber === FormStep.SECOND && (
					<StepSecond
						bookingSettings={bookingSettings}
						formState={formState}
						formError={formError}
						isLoading={isLoading}
						onInputChange={onInputChange}
						onInputFieldChange={onInputFieldChange}
						onReserveBtnClick={onReserveBtnClick}
						onMarketingChange={onChangeMarketing}
					/>
				)}

				{stepNumber === FormStep.PAY_WARNING && (
					<>
						<WarningBlock>{i18n('booking.checkout.warning')}</WarningBlock>
						{redirectUrl && (
							<a href={redirectUrl} target="_blank">
								<Button fullWidth size={'large'} appearance={'primary'}>
									{i18n('booking.proceed.checkout')}
								</Button>
							</a>
						)}
					</>
				)}

				{stepNumber === FormStep.ERROR && (
					<PaymentError isPaidBooking={!!bookingSettings.deposit?.active} />
				)}
			</div>
			<Dialog title={i18n('error')} visible={!!error} onClose={() => setError('')}>
				<div className={style.errorDialog}>
					<div className={style.errorDialogText}>{error}</div>
					<Button appearance={'primary'} onClick={() => setError('')}>
						{i18n('ok')}
					</Button>
				</div>
			</Dialog>
			<BlockTimeDurationModal
				onClose={handleCloseTimeDurationConflictModal}
				visible={timeDurationConflict}
			/>
			<PoweredBy className={style.poweredBy} />
		</div>
	);
};

export default Form;
